<!-- eslint-disable -->
<template>
  <div class="myday">
    <!-- ============ HEADER ============ -->
    <div class="myday-header">
      <div class="myday-header__left">
        <div class="myday-header__icon">
          <feather-icon icon="SunriseIcon" size="20" />
        </div>
        <div>
          <h2 class="myday-header__title">My Day</h2>
          <p class="myday-header__date">{{ prettyDate }}</p>
        </div>
      </div>

      <div class="myday-header__right">
        <button
          type="button"
          class="myday-toggle"
          :class="{ 'myday-toggle--team': showAll }"
          @click="toggleScope"
        >
          <feather-icon :icon="showAll ? 'UsersIcon' : 'UserIcon'" size="16" />
          <span>{{ showAll ? 'Whole team' : 'My items' }}</span>
        </button>

        <button type="button" class="myday-refresh" title="Refresh" @click="fetchDay">
          <feather-icon icon="RefreshCwIcon" size="16" />
        </button>
      </div>
    </div>

    <!-- ============ STAT CARDS ============ -->
    <!--
      Karim, 2 Sep: "everyone can see the count of himself only." These cards are
      always the viewer's OWN numbers — never the team's, even for a manager
      currently browsing everyone's overdue list below via "Show all".
    -->
    <div class="myday-stats">
      <div class="myday-stat myday-stat--danger">
        <div class="myday-stat__icon">
          <feather-icon icon="AlertTriangleIcon" size="18" />
        </div>
        <div class="myday-stat__body">
          <span class="myday-stat__value">{{ myCounts.overdue }}</span>
          <span class="myday-stat__label">My overdue</span>
        </div>
      </div>

      <div class="myday-stat myday-stat--warning">
        <div class="myday-stat__icon">
          <feather-icon icon="ClockIcon" size="18" />
        </div>
        <div class="myday-stat__body">
          <span class="myday-stat__value">{{ myCounts.today }}</span>
          <span class="myday-stat__label">Due today</span>
        </div>
      </div>

      <div class="myday-stat myday-stat--info">
        <div class="myday-stat__icon">
          <feather-icon icon="CalendarIcon" size="18" />
        </div>
        <div class="myday-stat__body">
          <span class="myday-stat__value">{{ myCounts.upcoming }}</span>
          <span class="myday-stat__label">Upcoming</span>
        </div>
      </div>
    </div>

    <!-- ============ LOADING ============ -->
    <div v-if="loading" class="myday-loading">
      <div class="myday-spinner"></div>
      <p>Loading your day...</p>
    </div>

    <!-- ============ EMPTY ============ -->
    <div v-else-if="isEmpty" class="myday-empty">
      <div class="myday-empty__icon">
        <feather-icon icon="CheckCircleIcon" size="34" />
      </div>
      <h3 class="myday-empty__title">You're all clear</h3>
      <p class="myday-empty__text">
        Nothing is due right now. Schedule a follow-up from the pipeline or the leads board to keep things moving.
      </p>
    </div>

    <!-- ============ SECTIONS ============ -->
    <!-- Filter: show everything, or just one bucket -->
    <div v-else-if="!loading" class="myday-filters">
      <button
        v-for="f in filterOptions"
        :key="f.key"
        type="button"
        class="myday-filter"
        :class="{ 'myday-filter--active': bucketFilter === f.key }"
        @click="bucketFilter = f.key"
      >
        <feather-icon :icon="f.icon" size="14" />
        <span>{{ f.label }}</span>
        <span class="myday-filter__count">{{ f.count }}</span>
      </button>

      <div class="myday-filters__spacer" />

      <div class="myday-search">
        <feather-icon icon="SearchIcon" size="14" class="myday-search__icon" />
        <input v-model="search" type="text" class="myday-search__input" placeholder="Search company or client…" />
      </div>

      <select v-model="typeFilter" class="myday-select">
        <option value="">All activity types</option>
        <option value="Call">Call</option>
        <option value="Email">Email</option>
        <option value="Visit">Visit</option>
        <option value="Meeting">Meeting</option>
      </select>

      <!-- Manager-only: assigned / unassigned. A rep never sees this because
           everything he can see is already his (Karim, 10 Aug). -->
      <select
        v-if="access.canViewTeam && showAll"
        v-model="assignedFilter"
        class="myday-select"
        title="Managers: find work that has nobody responsible for it"
        @change="fetchDay"
      >
        <option value="">Assigned or not</option>
        <option value="no">Unassigned only</option>
        <option value="yes">Assigned only</option>
      </select>

      <!-- Manager-only: which office. Karim, 2 Sep: "we need to define the client
           and sales by office." -->
      <select
        v-if="access.canViewTeam && showAll && officeOptions.length"
        v-model="officeFilter"
        class="myday-select"
      >
        <option value="">All offices</option>
        <option v-for="o in officeOptions" :key="o.id" :value="o.id">{{ o.name }}</option>
      </select>

      <!-- MID-CRM W4 — the sync suggests a call on every expiring certificate.
           Those are reminders, not the rep's plan; this hides them in one click. -->
      <button
        type="button"
        class="myday-filter"
        :class="{ 'myday-filter--active': mineOnly }"
        :title="mineOnly ? 'Showing only activities you scheduled' : 'Also showing renewal reminders created by the system'"
        @click="mineOnly = !mineOnly"
      >
        <feather-icon :icon="mineOnly ? 'UserCheckIcon' : 'UsersIcon'" size="14" />
        <span>{{ mineOnly ? 'My planned work' : 'Including reminders' }}</span>
      </button>

      <select v-model="pipelineFilter" class="myday-select">
        <option value="">All pipelines</option>
        <option value="leads">Leads</option>
        <option value="deals">New Deals</option>
        <option value="renewals">Renewals</option>
        <option value="tickets">Tickets</option>
      </select>
    </div>

    <!--
      MID-CRM: the server caps each bucket (200) so a rep with thousands of overdue
      items cannot blow up the page. Say so out loud — the header count and the list
      length disagreeing with no explanation is exactly the kind of thing that makes
      the app look untrustworthy.
    -->
    <div v-if="!loading && truncatedNote" class="myday-truncated">
      <feather-icon icon="AlertTriangleIcon" size="15" />
      <span>{{ truncatedNote }}</span>
    </div>

    <div v-if="!loading" class="myday-sections">
      <section
        v-for="section in sections"
        :key="section.key"
        class="myday-section"
      >
        <div class="myday-section__head">
          <span class="myday-section__dot" :class="'myday-section__dot--' + section.variant"></span>
          <h3 class="myday-section__title">{{ section.label }}</h3>
          <span class="myday-section__count" :class="'myday-section__count--' + section.variant">
            {{ section.items.length }}
          </span>
        </div>

        <div v-if="!section.items.length" class="myday-section__empty">
          Nothing here.
        </div>

        <div v-else class="myday-list">
          <div
            v-for="(item, idx) in section.items"
            :key="section.key + '-' + item.id + '-' + idx"
            class="myday-item"
            :class="'myday-item--' + section.variant"
            @click="openItem(item)"
          >
            <div class="myday-item__icon" :class="'myday-item__icon--' + section.variant">
              <feather-icon :icon="activityIcon(item.activity && item.activity.type)" size="16" />
            </div>

            <div class="myday-item__main">
              <div class="myday-item__toprow">
                <span class="myday-item__title">{{ item.title || 'Untitled' }}</span>
                <span class="myday-pill" :class="pipelineClass(item.pipeline)">
                  {{ pipelineLabel(item.pipeline) }}
                </span>
                <span v-if="item.stage" class="myday-stage">{{ item.stage }}</span>
              </div>

              <div class="myday-item__meta">
                <span v-if="item.client" class="myday-item__client">
                  <feather-icon icon="UserIcon" size="12" />
                  {{ item.client }}
                </span>
                <!-- Karim, 2 Sep: "sales people are not in the list but all the
                     company names that are in the list." Only worth showing in
                     team view — in "My items" it's always you. -->
                <span
                  v-if="showAll"
                  class="myday-item__owner"
                  :class="{ 'myday-item__owner--unassigned': !item.ownerName }"
                >
                  <feather-icon icon="BriefcaseIcon" size="12" />
                  {{ item.ownerName || 'Unassigned' }}
                  <template v-if="item.officeName">· {{ item.officeName }}</template>
                </span>
                <span class="myday-item__type">
                  {{ (item.activity && item.activity.type) || 'Follow-up' }}
                </span>
                <!-- W4: be explicit about what the rep did NOT schedule. -->
                <span
                  v-if="item.activity && item.activity.auto"
                  class="myday-item__auto"
                  title="Suggested by the system because a certificate is expiring — you did not schedule this"
                >
                  <feather-icon icon="ZapIcon" size="10" /> reminder
                </span>
              </div>

              <p v-if="item.activity && item.activity.note" class="myday-item__note">
                {{ item.activity.note }}
              </p>
            </div>

            <div class="myday-item__right">
              <span class="myday-item__due" :class="'myday-item__due--' + section.variant">
                <feather-icon icon="CalendarIcon" size="12" />
                {{ formatDue(item.dueDate || (item.activity && item.activity.dueDate)) }}
              </span>
              <button
                type="button"
                class="myday-done-btn"
                @click.stop="openDone(item)"
              >
                <feather-icon icon="CheckIcon" size="14" />
                Done
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- ============ DONE / NEXT ACTIVITY MODAL ============ -->
    <div
      v-if="doneItem"
      class="myday-backdrop"
      @mousedown.self="closeDone"
    >
      <div class="myday-modal">
        <div class="myday-modal__header">
          <div class="myday-modal__header-left">
            <div class="myday-modal__icon">
              <feather-icon icon="CheckCircleIcon" size="18" />
            </div>
            <div>
              <h3 class="myday-modal__title">Complete activity</h3>
              <p class="myday-modal__subtitle">{{ doneItem.title }}</p>
            </div>
          </div>
          <button type="button" class="myday-modal__close" @click="closeDone">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div class="myday-modal__body">
          <div class="myday-current">
            <div class="myday-current__row">
              <span class="myday-current__type">
                <feather-icon :icon="activityIcon(doneItem.activity && doneItem.activity.type)" size="13" />
                {{ (doneItem.activity && doneItem.activity.type) || 'Follow-up' }}
              </span>
              <span class="myday-current__date">
                {{ formatDue(doneItem.dueDate || (doneItem.activity && doneItem.activity.dueDate)) }}
              </span>
            </div>
            <p class="myday-current__note">
              {{ (doneItem.activity && doneItem.activity.note) || 'No notes recorded.' }}
            </p>
          </div>

          <div class="myday-field">
            <label class="myday-label">Outcome / result notes</label>
            <textarea
              v-model="form.outcomeNote"
              class="myday-textarea"
              rows="2"
              placeholder="What happened? (e.g. Client confirmed the quotation...)"
            ></textarea>
          </div>

          <div class="myday-divider">
            <span>Next activity</span>
          </div>

          <div class="myday-row">
            <div class="myday-field myday-field--grow">
              <label class="myday-label">Type</label>
              <select v-model="form.nextType" class="myday-input">
                <option v-for="t in activityTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div class="myday-field myday-field--grow">
              <label class="myday-label">Due date</label>
              <input v-model="form.nextDate" type="date" class="myday-input" />
            </div>
          </div>

          <div class="myday-field">
            <label class="myday-label">Next action notes</label>
            <textarea
              v-model="form.nextNote"
              class="myday-textarea"
              rows="2"
              placeholder="What needs to happen next?"
            ></textarea>
          </div>
        </div>

        <div class="myday-modal__footer">
          <button type="button" class="myday-btn myday-btn--ghost" @click="closeDone">Cancel</button>
          <button
            type="button"
            class="myday-btn myday-btn--primary"
            :disabled="saving"
            @click="submitDone"
          >
            <feather-icon icon="CheckCircleIcon" size="15" />
            {{ saving ? 'Saving...' : 'Save & schedule' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ACTIVITY_ICONS = {
  Call: 'PhoneIcon',
  Email: 'MailIcon',
  Visit: 'MapPinIcon',
  Meeting: 'UsersIcon',
}

export default {
  name: 'MyDayCRM',
  data() {
    return {
      access: { canViewTeam: false },
      loading: false,
      saving: false,
      showAll: false,

      date: '',
      overdue: [],
      today: [],
      upcoming: [],
      counts: { overdue: 0, today: 0, upcoming: 0 },
      myCounts: { overdue: 0, today: 0, upcoming: 0 },
      bucketFilter: 'all',
      // Manager view only (Karim, 10 Aug): '' = everyone, 'no' = unassigned,
      // 'yes' = already assigned. A salesperson never sees this — everything he
      // can see is already his.
      assignedFilter: '',
      // Karim, 2 Sep: "we need to define the client and sales by office." Manager
      // view only, same reasoning as assignedFilter above.
      officeFilter: '',
      // W4: default OFF so nothing is hidden until the rep asks for it.
      mineOnly: false,
      search: '',
      typeFilter: '',
      pipelineFilter: '',

      doneItem: null,
      form: {
        outcomeNote: '',
        nextType: 'Call',
        nextDate: '',
        nextNote: '',
      },

      activityTypes: ['Call', 'Email', 'Visit', 'Meeting'],
    }
  },

  computed: {
    prettyDate() {
      const d = this.date ? new Date(this.date) : new Date()
      if (isNaN(d.getTime())) return this.date
      return d.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },

    filterOptions() {
      return [
        { key: 'all', label: 'Everything', icon: 'LayersIcon', count: this.overdue.length + this.today.length + this.upcoming.length },
        { key: 'overdue', label: 'Overdue', icon: 'AlertCircleIcon', count: this.overdue.length },
        { key: 'today', label: 'Due today', icon: 'SunIcon', count: this.today.length },
        { key: 'upcoming', label: 'Upcoming', icon: 'CalendarIcon', count: this.upcoming.length },
      ]
    },
    sections() {
      const all = [
        { key: 'overdue', label: 'Overdue', variant: 'danger', items: this.applyFilters(this.overdue) },
        { key: 'today', label: 'Today', variant: 'primary', items: this.applyFilters(this.today) },
        { key: 'upcoming', label: 'Upcoming', variant: 'info', items: this.applyFilters(this.upcoming) },
      ]
      return this.bucketFilter === 'all' ? all : all.filter(s => s.key === this.bucketFilter)
    },
    unusedSections() {
      return [
        { key: 'overdue', label: 'Overdue', variant: 'danger', items: this.overdue },
        { key: 'today', label: 'Today', variant: 'primary', items: this.today },
        { key: 'upcoming', label: 'Upcoming', variant: 'info', items: this.upcoming },
      ]
    },

    isEmpty() {
      return !this.overdue.length && !this.today.length && !this.upcoming.length
    },

    /** Distinct offices actually present in what's loaded — Karim, 2 Sep: "define
     * the client and sales by office." Built from the items themselves rather than
     * a separate office list call, so it never shows an office with nothing due. */
    officeOptions() {
      const seen = new Map()
      const all = [...this.overdue, ...this.today, ...this.upcoming]
      all.forEach(i => {
        if (i.officeId && !seen.has(i.officeId)) seen.set(i.officeId, i.officeName || `Office #${i.officeId}`)
      })
      return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
    },

    /** Honest note when the server sent fewer rows than it counted. */
    truncatedNote() {
      const parts = []
      if (this.counts.overdue > this.overdue.length) {
        parts.push(`${this.overdue.length} of ${this.counts.overdue} overdue`)
      }
      if (this.counts.today > this.today.length) {
        parts.push(`${this.today.length} of ${this.counts.today} due today`)
      }
      if (!parts.length) return ''
      return `Showing ${parts.join(' and ')}. Work through these and refresh to load more — `
        + 'use the filters to narrow it down.'
    },
  },

  created() {
    this.fetchDay()
  },

  methods: {

    async fetchAccess() {

      try {

        const res = await axios.get('crm/me')

        const body = decryptData(res.data.payload)

        if (body && body.data) this.access = body.data

      } catch (e) {}

    },
    notify(title, text, variant = 'success', icon = 'CheckCircleIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, text, variant, icon },
      })
    },

    activityIcon(type) {
      return ACTIVITY_ICONS[type] || 'ClockIcon'
    },

    pipelineLabel(pipeline) {
      const map = {
        leads: 'Leads',
        opportunities: 'Opportunities',
        renewals: 'Renewals',
        deals: 'Deals',
        tickets: 'Tickets',
      }
      return map[pipeline] || (pipeline || 'CRM')
    },

    pipelineClass(pipeline) {
      const map = {
        leads: 'myday-pill--info',
        opportunities: 'myday-pill--primary',
        renewals: 'myday-pill--warning',
        deals: 'myday-pill--success',
        tickets: 'myday-pill--danger',
      }
      return map[pipeline] || 'myday-pill--muted'
    },

    formatDue(value) {
      if (!value) return 'No date'
      const d = new Date(value)
      if (isNaN(d.getTime())) return String(value)
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    applyFilters(list) {
      let r = Array.isArray(list) ? [...list] : []
      if (this.search) {
        const q = this.search.toLowerCase()
        r = r.filter(i =>
          (i.title && i.title.toLowerCase().includes(q)) ||
          (i.client && i.client.toLowerCase().includes(q))
        )
      }
      if (this.typeFilter) {
        r = r.filter(i => i.activity && i.activity.type === this.typeFilter)
      }
      if (this.pipelineFilter) {
        r = r.filter(i => i.pipeline === this.pipelineFilter)
      }
      if (this.officeFilter) {
        r = r.filter(i => String(i.officeId) === String(this.officeFilter))
      }
      // MID-CRM W4 — "My planned work only": hide the reminders the sync generated
      // for expiring certificates, so the list is what the rep actually scheduled.
      if (this.mineOnly) {
        r = r.filter(i => !(i.activity && i.activity.auto))
      }
      return r
    },

    todayIso() {
      const d = new Date()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },

    plusDaysIso(days) {
      const d = new Date()
      d.setDate(d.getDate() + days)
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },

    toggleScope() {
      this.showAll = !this.showAll
      this.fetchDay()
    },

    async fetchDay() {
      this.loading = true
      try {
        const params = {}
        if (this.showAll) params.all = 1
        // Managers only: "who still has no owner?" is their main question.
        if (this.showAll && this.assignedFilter) params.assigned = this.assignedFilter

        const res = await axios.get('crm/my-day', { params })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}

        this.date = data.date || this.todayIso()
        this.overdue = Array.isArray(data.overdue) ? data.overdue : []
        this.today = Array.isArray(data.today) ? data.today : []
        this.upcoming = Array.isArray(data.upcoming) ? data.upcoming : []

        const c = data.counts || {}
        this.counts = {
          overdue: typeof c.overdue === 'number' ? c.overdue : this.overdue.length,
          today: typeof c.today === 'number' ? c.today : this.today.length,
          upcoming: typeof c.upcoming === 'number' ? c.upcoming : this.upcoming.length,
        }
        // Karim, 2 Sep: "everyone can see the count of himself only." `counts`
        // follows whatever list is showing (team-wide for a manager by default —
        // that's the "see all overdue activity" part); `myCounts` is always this
        // person's own, which is what the stat cards at the top show.
        const mc = data.myCounts || {}
        this.myCounts = {
          overdue: typeof mc.overdue === 'number' ? mc.overdue : 0,
          today: typeof mc.today === 'number' ? mc.today : 0,
          upcoming: typeof mc.upcoming === 'number' ? mc.upcoming : 0,
        }
      } catch (e) {
        this.overdue = []
        this.today = []
        this.upcoming = []
        this.counts = { overdue: 0, today: 0, upcoming: 0 }
        this.myCounts = { overdue: 0, today: 0, upcoming: 0 }
        this.notify('Load failed', 'Could not load your day', 'danger', 'AlertCircleIcon')
      } finally {
        this.loading = false
      }
    },

    openItem(item) {
      if (!item) return
      if (item.pipeline === 'leads' || item.kind === 'lead') {
        this.$router.push({ name: 'crm-system-leads', query: { lead: item.id } }).catch(() => {})
      } else {
        this.$router.push({ name: 'crm-system-pipeline', query: { card: item.id, pipeline: item.pipeline } }).catch(() => {})
      }
    },

    openDone(item) {
      this.doneItem = item
      this.form = {
        outcomeNote: '',
        nextType: (item.activity && item.activity.type) || 'Call',
        nextDate: this.plusDaysIso(3),
        nextNote: '',
      }
    },

    closeDone() {
      this.doneItem = null
      this.saving = false
    },

    leadIdOf(item) {
      const raw = String(item.id || '')
      const match = raw.match(/(\d+)/)
      return match ? match[1] : null
    },

    async submitDone() {
      if (!this.doneItem || this.saving) return
      this.saving = true

      const item = this.doneItem
      const nextActivity = {
        type: this.form.nextType,
        dueDate: this.form.nextDate,
        note: this.form.nextNote,
        status: 'Pending',
      }

      try {
        if (item.kind === 'lead' || item.pipeline === 'leads') {
          const leadId = this.leadIdOf(item)
          if (!leadId) throw new Error('Invalid lead id')
          await axios.post(`crm/leads/${leadId}/activity`, {
            outcomeNote: this.form.outcomeNote,
            nextActivity,
          })
        } else {
          await axios.post('crm/activities', {
            cardId: item.id,
            pipeline: item.pipeline,
            outcomeNote: this.form.outcomeNote,
            nextActivity,
            targetStage: null,
            user: localStorage.getItem('name') || '',
          })
        }

        this.notify(
          'Activity completed',
          `Next ${nextActivity.type} on ${this.formatDue(nextActivity.dueDate)}`
        )
        this.closeDone()
        await this.fetchDay()
      } catch (e) {
        this.notify('Save failed', 'Could not save the activity', 'danger', 'AlertCircleIcon')
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$danger: #ea5455;
$warning: #3b82f6;
$info: #00cfe8;
$success: #28c76f;
$body: #6e6b7b;
$heading: #5e5873;
$border: #ebe9f1;
$radius: 0.6rem;
$shadow: 0 2px 8px rgba(34, 41, 47, 0.08);

.myday {
  padding: 0.25rem 0 2rem 0;
}

/* ---------- HEADER ---------- */
.myday-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1.25rem;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: $radius;
    background: rgba($primary, 0.12);
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__title {
    font-size: 1.35rem;
    font-weight: 600;
    color: $heading;
    margin: 0;
    line-height: 1.2;
  }

  &__date {
    font-size: 0.85rem;
    color: $body;
    margin: 0.15rem 0 0 0;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.myday-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: $radius;
  border: 1px solid $primary;
  background: rgba($primary, 0.08);
  color: $primary;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba($primary, 0.16);
  }

  &--team {
    background: $primary;
    color: #fff;

    &:hover {
      background: darken($primary, 6%);
    }
  }
}

.myday-refresh {
  width: 36px;
  height: 36px;
  border-radius: $radius;
  border: 1px solid $border;
  background: #fff;
  color: $body;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: $primary;
    border-color: $primary;
  }
}

/* ---------- STATS ---------- */
.myday-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.myday-stat {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 1.05rem 1.15rem;

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__body {
    display: flex;
    flex-direction: column;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 600;
    color: $heading;
    line-height: 1.1;
  }

  &__label {
    font-size: 0.82rem;
    color: $body;
  }

  &--danger .myday-stat__icon {
    background: rgba($danger, 0.12);
    color: $danger;
  }
  &--warning .myday-stat__icon {
    background: rgba($warning, 0.12);
    color: $warning;
  }
  &--info .myday-stat__icon {
    background: rgba($info, 0.12);
    color: $info;
  }
}

/* ---------- LOADING ---------- */
.myday-loading {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 3rem 1rem;
  text-align: center;
  color: $body;

  p {
    margin: 0.9rem 0 0 0;
    font-size: 0.9rem;
  }
}

.myday-spinner {
  width: 34px;
  height: 34px;
  margin: 0 auto;
  border-radius: 50%;
  border: 3px solid rgba($primary, 0.2);
  border-top-color: $primary;
  animation: myday-spin 0.7s linear infinite;
}

@keyframes myday-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- EMPTY ---------- */
.myday-empty {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 3rem 1.5rem;
  text-align: center;

  &__icon {
    width: 72px;
    height: 72px;
    margin: 0 auto 1rem auto;
    border-radius: 50%;
    background: rgba($success, 0.12);
    color: $success;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: 1.15rem;
    font-weight: 600;
    color: $heading;
    margin: 0 0 0.4rem 0;
  }

  &__text {
    font-size: 0.87rem;
    color: $body;
    margin: 0 auto;
    max-width: 420px;
  }
}

/* ---------- SECTIONS ---------- */
.myday-sections {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.myday-section {
  background: #fff;
  border-radius: $radius;
  box-shadow: $shadow;
  padding: 1.1rem 1.15rem;

  &__head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.9rem;
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;

    &--danger { background: $danger; }
    &--warning { background: $warning; }
    &--info { background: $info; }
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $heading;
    margin: 0;
  }

  &__count {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.12rem 0.5rem;
    border-radius: 1rem;

    &--danger { background: rgba($danger, 0.12); color: $danger; }
    &--warning { background: rgba($warning, 0.12); color: $warning; }
    &--info { background: rgba($info, 0.12); color: $info; }
  }

  &__empty {
    font-size: 0.83rem;
    color: lighten($body, 12%);
    padding: 0.5rem 0.1rem;
  }
}

.myday-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.myday-item {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid $border;
  border-left-width: 3px;
  border-radius: $radius;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fff;

  &:hover {
    box-shadow: $shadow;
    transform: translateY(-1px);
  }

  &--danger { border-left-color: $danger; }
  &--warning { border-left-color: $warning; }
  &--info { border-left-color: $info; }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: $radius;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--danger { background: rgba($danger, 0.12); color: $danger; }
    &--warning { background: rgba($warning, 0.12); color: $warning; }
    &--info { background: rgba($info, 0.12); color: $info; }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__toprow {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  &__title {
    font-size: 0.92rem;
    font-weight: 600;
    color: $heading;
  }

  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 0.2rem;
    font-size: 0.78rem;
    color: $body;
  }

  &__client {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  &__type {
    font-weight: 600;
  }

  &__note {
    margin: 0.35rem 0 0 0;
    font-size: 0.8rem;
    color: lighten($body, 8%);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.45rem;
    flex-shrink: 0;
  }

  &__due {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;

    &--danger { color: $danger; }
    &--warning { color: $warning; }
    &--info { color: $info; }
  }
}

.myday-pill {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: 1rem;
  text-transform: capitalize;

  &--primary { background: rgba($primary, 0.12); color: $primary; }
  &--info { background: rgba($info, 0.12); color: $info; }
  &--warning { background: rgba($warning, 0.12); color: $warning; }
  &--success { background: rgba($success, 0.12); color: $success; }
  &--danger { background: rgba($danger, 0.12); color: $danger; }
  &--muted { background: rgba($body, 0.12); color: $body; }
}

.myday-stage {
  font-size: 0.7rem;
  color: $body;
  border: 1px solid $border;
  border-radius: 1rem;
  padding: 0.1rem 0.45rem;
}

.myday-done-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.32rem 0.7rem;
  border-radius: $radius;
  border: 1px solid $success;
  background: rgba($success, 0.1);
  color: $success;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: $success;
    color: #fff;
  }
}

/* ---------- MODAL ---------- */
.myday-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(34, 41, 47, 0.45);
}

.myday-modal {
  background: #fff;
  border-radius: $radius;
  box-shadow: 0 12px 40px rgba(34, 41, 47, 0.24);
  width: 100%;
  max-width: 520px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.15rem;
    border-bottom: 1px solid $border;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: $radius;
    background: rgba($success, 0.12);
    color: $success;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $heading;
    margin: 0;
  }

  &__subtitle {
    font-size: 0.78rem;
    color: $body;
    margin: 0.1rem 0 0 0;
  }

  &__close {
    width: 32px;
    height: 32px;
    border-radius: $radius;
    border: none;
    background: rgba($body, 0.08);
    color: $body;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: rgba($danger, 0.12);
      color: $danger;
    }
  }

  &__body {
    padding: 1.1rem 1.15rem;
    overflow-y: auto;
    flex: 1;
  }

  &__footer {
    padding: 0.9rem 1.15rem;
    border-top: 1px solid $border;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}

.myday-current {
  background: rgba($primary, 0.05);
  border: 1px solid $border;
  border-radius: $radius;
  padding: 0.7rem 0.8rem;
  margin-bottom: 1rem;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.3rem;
  }

  &__type {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: $primary;
  }

  &__date {
    font-size: 0.75rem;
    color: $body;
  }

  &__note {
    margin: 0;
    font-size: 0.8rem;
    color: $body;
  }
}

.myday-divider {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 1rem 0 0.85rem 0;

  span {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $body;
    white-space: nowrap;
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: $border;
  }
}

.myday-row {
  display: flex;
  gap: 0.65rem;
}

.myday-field {
  margin-bottom: 0.85rem;

  &--grow {
    flex: 1;
    min-width: 0;
  }
}

.myday-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: $heading;
  margin-bottom: 0.3rem;
}

.myday-input,
.myday-textarea {
  width: 100%;
  border: 1px solid $border;
  border-radius: $radius;
  padding: 0.5rem 0.65rem;
  font-size: 0.85rem;
  color: $body;
  background: #fff;
  outline: none;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba($primary, 0.15);
  }
}

.myday-textarea {
  resize: vertical;
}

.myday-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: $radius;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &--ghost {
    background: #fff;
    border-color: $border;
    color: $body;

    &:hover {
      background: rgba($body, 0.06);
    }
  }

  &--primary {
    background: $primary;
    color: #fff;

    &:hover {
      background: darken($primary, 6%);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 767px) {
  .myday-stats {
    grid-template-columns: 1fr;
  }

  .myday-item {
    flex-wrap: wrap;
  }

  .myday-item__right {
    flex-direction: row;
    align-items: center;
    width: 100%;
    justify-content: space-between;
  }

  .myday-row {
    flex-direction: column;
    gap: 0;
  }
}
.myday-filters {
  display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; margin-bottom: 1rem;
}
.myday-filters__spacer { flex: 1 1 auto; }
.myday-filter {
  display: inline-flex; align-items: center; gap: .35rem; cursor: pointer;
  border: 1px solid #e4e4ec; background: #fff; color: #6e6b7b;
  border-radius: 999px; padding: .35rem .75rem; font-size: .8rem; font-weight: 600;
}
.myday-filter:hover { border-color: #2563eb; color: #2563eb; }
.myday-filter--active { background: #2563eb; border-color: #2563eb; color: #fff; }
.myday-filter__count {
  background: rgba(0,0,0,.08); border-radius: 999px; padding: 0 .4rem; font-size: .72rem;
}
.myday-filter--active .myday-filter__count { background: rgba(255,255,255,.25); }
.myday-item__auto {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: .35rem;
  padding: .05rem .35rem;
  border-radius: 10px;
  background: rgba(37, 99, 235, .12);
  color: #2563eb;
  font-size: .62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.myday-item__owner {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: .05rem .4rem;
  border-radius: 10px;
  background: rgba(40, 199, 111, .12);
  color: #1f9254;
  font-size: .72rem;
  font-weight: 600;

  &--unassigned {
    background: rgba(234, 84, 85, .1);
    color: #ea5455;
  }
}
.myday-truncated {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin: 0 0 .75rem;
  padding: .55rem .8rem;
  border-radius: .5rem;
  background: rgba(59, 130, 246, .12);
  color: #1e40af;
  font-size: .8rem;
  font-weight: 600;
}
.myday-search { position: relative; }
.myday-search__icon { position: absolute; left: .5rem; top: 50%; transform: translateY(-50%); color: #b9b9c3; }
.myday-search__input {
  border: 1px solid #e4e4ec; border-radius: .35rem; padding: .35rem .6rem .35rem 1.75rem;
  font-size: .8rem; min-width: 200px;
}
.myday-search__input:focus { outline: none; border-color: #2563eb; }
.myday-select {
  border: 1px solid #e4e4ec; border-radius: .35rem; padding: .35rem .5rem;
  font-size: .8rem; background: #fff; color: #5e5873;
}
</style>
